import { Link } from 'react-router-dom'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { BILLINGS_UPLOAD_PATH } from '@/components/sales-trackers/billings-empty'

/**
 * "Nothing has been uploaded yet" is an answer, not a failure.
 *
 * WHY THIS EXISTS. Every Sales Trackers page resolves its fiscal year from the data that
 * exists (`resolveFiscalYear` in routes/billings.js), and with no billings at all that
 * throws a deliberate 404 carrying real instructions. The pages rendered it as a
 * destructive "Could not load the overview" alert, which on the first production deploy
 * meant a red box asserting a record had been deleted, on a database where nothing had
 * ever been created.
 *
 * Worse, Overview returned early, which also hid the "Upload QuickBooks export" button —
 * the only clickable route to the upload page, which is deliberately kept out of the nav
 * band. The one screen a new database shows was a dead end whose only escape was typing a
 * URL.
 *
 * So the empty state says what is true, and keeps the way forward on screen.
 */
export function NoBillingsYet({
  /** What this particular page would have shown. Keeps the copy specific. */
  what = 'These figures',
}: {
  what?: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>No billings have been uploaded yet</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {what} come from the QuickBooks Net Billings export. Upload one and this page fills itself
          in. Nothing is wrong.
        </p>
        <Button asChild>
          <Link to={BILLINGS_UPLOAD_PATH}>
            <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
            Upload QuickBooks export
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
